import {PageEventView} from '@domain/model/Event/domain/model/UiEvents';
import {
  AppEvent,
  ExperimentEvent,
  MyLookieroActionEvent,
  NavigationEvent,
  PromoCodeEvent,
  UserEvent,
} from '@domain/model/Event';
import {PAY_EVENTS} from '@domain/model/Event/domain/model/PayEvent';
import {Section} from '@lookiero/payments-front';
import {DeriskerEvent} from '@domain/model/Event/domain/model/DeriskerEvent';

const VIEW_TO_SECTION: Record<PageEventView, string> = Object.freeze({
  [PageEventView.LOGIN]: 'login',
  [PageEventView.ACCOUNT]: 'account_menu',
  [PageEventView.CHECKOUT_PREVIEW]: 'myLK_checkoutPreview',
  [PageEventView.SUBSCRIPTION]: 'myLK_Order',
  [PageEventView.FIRST_SUBSCRIPTION]: 'myLK_1stOrder',
  [PageEventView.FIRST_SCHEDULE]: 'myLK_1stCalendarOrder',
  [PageEventView.SCHEDULE]: 'myLK_CalendarOrder',
  [PageEventView.SHIP_AND_PAY]: 'myLK_shipAndPay',
  [PageEventView.REGISTER]: 'signUp',
  [PageEventView.FIRST_SHIP_AND_PAY]: 'myLK_1stShipAndPay',
  [PageEventView.REFERRAL]: 'referral',
  [PageEventView.LANDING]: 'referral_landing',
  [PageEventView.FIRST_SHIP]: 'myLK_1stShip',
  [PageEventView.SHIP]: 'myLK_Ship',
  [PageEventView.SURVEY]: 'surveyHowDidYouMeetUs',

  [PageEventView.FIRST_PAY]: 'myLK_1stPay',
  [PageEventView.PAY]: 'myLK_Pay',
  [PageEventView.PLACED_ORDER]: 'myLK_OrderPlaced',
  [PageEventView.TRACKING]: 'myLK_OrderPlacedPS',
  [PageEventView.OPEN_MENU]: 'BurgerMenu',
  [PageEventView.PROFILE_MENU]: 'ProfileMenu',

  [PageEventView.NOT_FOUND]: '404',
  [PageEventView.LOOK_AND_LIKE]: 'lookAndLike',
  [PageEventView.INSPIRATION]: 'inspiration',
  [PageEventView.INBOX]: 'inbox',
  [PageEventView.DELETE_ACCOUNT]: 'delete-account',
  [PageEventView.DELETE_ACCOUNT_CONFIRM]: 'delete-account-confirm',
  [PageEventView.DELETE_ACCOUNT_SUCCESS]: 'delete-account-success',
  [PageEventView.DELETE_ACCOUNT_ERROR]: 'delete-account-error',
  [PageEventView.CANCEL_BOX]: 'cancel_box_info',
  [PageEventView.CANCEL_BOX_CONFIRM]: 'cancel_box_confirm',
  [PageEventView.CANCEL_BOX_SUCCESS]: 'cancel_box_success',
  [PageEventView.CANCEL_BOX_ERROR]: 'cancel_box_error',
  [PageEventView.SUPPORT]: 'support',
  [PageEventView.FAQS]: 'faqs',
});

const TRANSACTIONAL_ACTIONS = [
  UserEvent.ORDER_CANCELLED,
  UserEvent.ORDER_PLACED,
  UserEvent.ORDER_SKIPPED,
  UserEvent.SUBSCRIPTION_CANCELLED,
  UserEvent.SUBSCRIPTION_PAUSED,
];
const TRANSACTIONAL_ACTION_TO_NAME: Record<string, string> = {
  [UserEvent.ORDER_CANCELLED]: 'orderCancelled',
  [UserEvent.ORDER_PLACED]: 'orderPlaced',
  [UserEvent.ORDER_SKIPPED]: 'orderSkipped',
  [UserEvent.SUBSCRIPTION_CANCELLED]: 'cancelSubscription',
  [UserEvent.SUBSCRIPTION_PAUSED]: 'pauseSubscription',
};

const SESSION_ACTIONS = [UserEvent.LOGIN, UserEvent.LOGOUT, UserEvent.REGISTER] as const;
const SESSION_ACTION_TO_NAME: Record<string, string> = {
  [UserEvent.LOGIN]: 'signIn',
  [UserEvent.REGISTER]: 'signUp',
  [UserEvent.LOGOUT]: 'signOut',
};

const NAVIGATION_ACTIONS = [
  NavigationEvent.QUIZ_STARTED,
  NavigationEvent.QUIZ_FINISHED,
  NavigationEvent.QUIZ_STEP_SUBMIT,
  NavigationEvent.ONBOARDING_STARTED,
  NavigationEvent.ONBOARDING_FINISHED,
  NavigationEvent.RANGE_PRICES,
  NavigationEvent.IS_MOTHER,
  NavigationEvent.SUBSCRIPTION_TAP_2WEEKS,
  NavigationEvent.SUBSCRIPTION_TAP_MONTHLY,
  NavigationEvent.SUBSCRIPTION_TAP_BIMONTHLY,
  NavigationEvent.SUBSCRIPTION_TAP_QUARTERLY,
  NavigationEvent.SUBSCRIPTION_TAP_TOGGLE,
];

const NAVIGATION_USER_ACTIONS = [
  NavigationEvent.REFERRAL_CODE_SHARE,
  NavigationEvent.DOWNLOAD_APP_BUTTONS,
  NavigationEvent.VIEW_POST_ORDER_MODAL,
  NavigationEvent.CLOSE_POST_ORDER_MODAL,
  NavigationEvent.MANAGE_PLAN,
  NavigationEvent.FB_SIGN_UP_BUTTON,
  NavigationEvent.DELETE_ACCOUNT_LINK,
];

const NAVIGATION_ACTION_TO_NAME: Record<string, string> = {
  [NavigationEvent.QUIZ_STARTED]: 'quizStarted',
  [NavigationEvent.QUIZ_FINISHED]: 'quizFinished',
  [NavigationEvent.QUIZ_STEP_SUBMIT]: 'quiz',
  [NavigationEvent.ONBOARDING_STARTED]: 'onBoardingStarted',
  [NavigationEvent.ONBOARDING_FINISHED]: 'onBoardingFinished',
  [NavigationEvent.RANGE_PRICES]: 'rangePrices',
  [NavigationEvent.IS_MOTHER]: 'isMother',
  [NavigationEvent.REFERRAL_CODE_SHARE]: 'referralcodeshare',
  [NavigationEvent.DOWNLOAD_APP_BUTTONS]: 'downloadAppButton',
  [NavigationEvent.VIEW_POST_ORDER_MODAL]: 'myLKPostOrderModalView',
  [NavigationEvent.CLOSE_POST_ORDER_MODAL]: 'myLKPostOrderModalClose',
  [NavigationEvent.MANAGE_PLAN]: 'managePlan',
  [NavigationEvent.FB_SIGN_UP_BUTTON]: 'FBSignUpButton',
  [NavigationEvent.DELETE_ACCOUNT_LINK]: 'delete-account-link',
  [NavigationEvent.SUBSCRIPTION_TAP_2WEEKS]: 'subscriptionTap2weeks',
  [NavigationEvent.SUBSCRIPTION_TAP_MONTHLY]: 'subscriptionTapMonthly',
  [NavigationEvent.SUBSCRIPTION_TAP_BIMONTHLY]: 'subscriptionTapBimonthly',
  [NavigationEvent.SUBSCRIPTION_TAP_QUARTERLY]: 'subscriptionTapQuarterly',
  [NavigationEvent.SUBSCRIPTION_TAP_TOGGLE]: 'subscriptionTapToggle',
};

const DERISKER_ACTIONS = [DeriskerEvent.INFOBOX_PRESSED, DeriskerEvent.INFOBOX_IMPRESSION];

const DERISKER_ACTION_TO_SECTION: Record<string, string> = {
  [DeriskerEvent.INFOBOX_PRESSED]: 'myLK_OrderPlaced',
  [DeriskerEvent.INFOBOX_IMPRESSION]: 'myLK_OrderPlaced',
};

const MY_LOOKIERO_EVENTS: MyLookieroActionEvent[] = [
  MyLookieroActionEvent.OPEN_CHECKOUT,
  MyLookieroActionEvent.PREVIEW_CLICK,
  MyLookieroActionEvent.STEPPER_CLICK,
  MyLookieroActionEvent.SEND_COMMENT,
];

const HOME_VIEWS = [PageEventView.SUBSCRIPTION, PageEventView.FIRST_SUBSCRIPTION];

const EXPERIMENT_ACTIONS = [ExperimentEvent.EXPERIMENT_APPLIED];

const APP_ACTIONS = [AppEvent.ACCEPT_UPDATE_VERSION, AppEvent.SHOW_UPDATE_VERSION, AppEvent.SKIP_UPDATE_VERSION];

const PROMO_CODE_ACTIONS = [PromoCodeEvent.PROMO_CODE_APPLIED];

const PAY_ACTIONS = [...PAY_EVENTS];

const PAYMENTS_SECTION_TO_TRACKING_SECTION: Record<string, string> = {
  [Section.DIRECT_BUY_CHECKOUT]: 'shop_shop-checkout',
  [Section.BOX_CHECKOUT]: 'checkout_checkout',
  [Section.MY_ACCOUNT]: 'account_menu',
  [Section.BOX_OFON]: 'myLK_OrderPlaced',
  [Section.BOX_PLACE_ORDER]: 'myLK_Pay',
  [`first-${Section.BOX_PLACE_ORDER}`]: 'MyLK_1stPay',
};

const SEGMENT = {
  NEUTRAL: 'NEUTRAL',
};

export {
  APP_ACTIONS,
  EXPERIMENT_ACTIONS,
  HOME_VIEWS,
  NAVIGATION_ACTION_TO_NAME,
  NAVIGATION_ACTIONS,
  NAVIGATION_USER_ACTIONS,
  SESSION_ACTION_TO_NAME,
  SESSION_ACTIONS,
  TRANSACTIONAL_ACTIONS,
  TRANSACTIONAL_ACTION_TO_NAME,
  VIEW_TO_SECTION,
  PROMO_CODE_ACTIONS,
  MY_LOOKIERO_EVENTS,
  PAY_ACTIONS,
  PAYMENTS_SECTION_TO_TRACKING_SECTION,
  SEGMENT,
  DERISKER_ACTIONS,
  DERISKER_ACTION_TO_SECTION,
};
